/**
 * Internal Profiles Hook
 * React hook for fetching internal user profiles (employees) for a specific organization
 * Uses smart caching by organizationId to avoid duplicate API calls across components
 * 
 * Note: For client profiles, use useClientProfiles() hook
 * Note: For worker profiles, use useWorkerProfiles() hook
 */

import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/lib/api'
import { PROFILE_STATUS, PROFILE_TYPE } from '@/lib/constants/profile-codes'

export interface InternalProfile {
  Id: number
  ProfileStatusId: number
  ContactId: number
  ProfileTypeId: number
  OrganizationId: number
  FullName: string
  DisplayValue: string
}

interface InternalProfileApiItem {
  Id: number
  ProfileStatusId: number
  ContactId: number
  ProfileTypeId: number
  OrganizationId: number
  Contact: {
    FullName: string
  }
}

interface InternalProfilesApiResponse {
  Items: InternalProfileApiItem[]
}

interface CachedInternalProfiles {
  internalProfiles: InternalProfile[]
  loading: boolean
  error: string | null
  timestamp: number
}

interface UseInternalProfilesParams {
  organizationId: number
}

interface UseInternalProfilesReturn {
  loading: boolean
  error: string | null
  internalProfiles: InternalProfile[]
  refresh: () => Promise<void>
}

// Global cache outside the hook - shared across all instances
const internalProfilesCache = new Map<number, CachedInternalProfiles>()

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000

// Set of subscribers for cache updates
const subscribers = new Set<() => void>()

// Notify all subscribers when cache updates
const notifySubscribers = () => {
  subscribers.forEach(callback => callback())
}

export function useInternalProfiles({
  organizationId
}: UseInternalProfilesParams): UseInternalProfilesReturn {
  const { mainApi } = useApi()
  const [, forceUpdate] = useState(0)
  
  // Subscribe to cache updates
  useEffect(() => {
    const updateCallback = () => forceUpdate(prev => prev + 1)
    subscribers.add(updateCallback)
    
    return () => {
      subscribers.delete(updateCallback)
    }
  }, [])

  const fetchInternalProfiles = async (orgId: number): Promise<InternalProfile[]> => {
    // This function should only be called with valid organization IDs
    // If orgId is 0, it indicates a programming error since fetchAndCache handles that case
    if (!orgId || orgId === 0) {
      throw new Error(`fetchInternalProfiles called with invalid organizationId: ${orgId}`)
    }

    console.log(`Fetching internal user profiles for organization ${orgId}...`)

    const oDataParams = new URLSearchParams({
      '$expand': 'Contact',
      '$filter': `OrganizationId eq ${orgId} and ProfileTypeId eq ${PROFILE_TYPE.INTERNAL} and (ProfileStatusId eq ${PROFILE_STATUS.ACTIVE} or ProfileStatusId eq ${PROFILE_STATUS.PENDING_CHANGE})`,
      '$select': 'Id,ProfileStatusId,ContactId,ProfileTypeId,OrganizationId,Contact/FullName'
    })
    
    const endpoint = `api/UserProfile/2/UserProfiles?${oDataParams.toString()}` // Profile Type 2 = Internal
    const response = await mainApi.get<InternalProfilesApiResponse>(endpoint)
    
    const profiles = response?.Items || []
    
    const mappedProfiles: InternalProfile[] = profiles.map((p: InternalProfileApiItem) => ({
      Id: p.Id,
      ProfileStatusId: p.ProfileStatusId,
      ContactId: p.ContactId,
      ProfileTypeId: p.ProfileTypeId,
      OrganizationId: p.OrganizationId,
      FullName: p.Contact?.FullName || 'Unknown',
      DisplayValue: `${p.Contact?.FullName || 'Unknown'} - ${p.Id}`
    }))
    
    console.log(`Loaded ${mappedProfiles.length} internal user profiles for organization ${orgId}`)
    return mappedProfiles
  }

  const fetchAndCache = useCallback(async (orgId: number) => {
    // This should not be called with organizationId 0 since useEffect prevents it
    if (!orgId || orgId === 0) {
      console.warn('fetchAndCache called with invalid organizationId:', orgId)
      return
    }

    console.log(`Fetching internal user profiles for organization ${orgId}...`)
    
    // Set loading state in cache
    const existingCache = internalProfilesCache.get(orgId)
    internalProfilesCache.set(orgId, {
      internalProfiles: existingCache?.internalProfiles || [],
      loading: true,
      error: null,
      timestamp: Date.now()
    })
    notifySubscribers()
    
    try {
      const internalProfiles = await fetchInternalProfiles(orgId)
      
      // Cache the successful results
      internalProfilesCache.set(orgId, {
        internalProfiles: internalProfiles,
        loading: false,
        error: null,
        timestamp: Date.now()
      })
      
      console.log(`Cached internal user profiles for organization ${orgId}`)
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load internal user profiles'
      
      // Cache the error state
      internalProfilesCache.set(orgId, {
        internalProfiles: [],
        loading: false,
        error: errorMessage,
        timestamp: Date.now()
      })
      
      console.error(`Failed to load internal user profiles for org ${orgId}:`, err)
    }
    
    notifySubscribers()
  }, [mainApi])

  const refresh = useCallback(async () => {
    await fetchAndCache(organizationId)
  }, [fetchAndCache, organizationId])

  // Check if data needs to be fetched
  useEffect(() => {
    // Don't fetch if organizationId is 0 (no organization selected yet)
    if (!organizationId || organizationId === 0) {
      return
    }
    
    const cache = internalProfilesCache.get(organizationId)
    const now = Date.now()
    
    // Fetch if no cache exists, cache is expired, or cache has an error
    if (!cache || 
        (now - cache.timestamp) > CACHE_TTL ||
        cache.error) {
      fetchAndCache(organizationId)
    }
  }, [organizationId, fetchAndCache])

  // Return current cache state or defaults
  const currentCache = internalProfilesCache.get(organizationId) || {
    internalProfiles: [],
    loading: organizationId === 0 ? false : true, // Don't show loading when no organization selected
    error: null,
    timestamp: 0
  }

  return {
    loading: currentCache.loading,
    error: currentCache.error,
    internalProfiles: currentCache.internalProfiles,
    refresh
  }
} 